/**
 * Orders an order line's modifier rows so sub-modifiers follow the option they
 * hang under, and reports how deep each one sits.
 *
 * A modifier option can carry nested modifier groups (sub-modifiers), gated on
 * the store flag `sub_modifiers_enabled`. On a saved order that nesting is
 * recorded per row as `parent_parser_order_modifier` — the id of the *parent
 * modifier row*, not the parent option. Rows still arrive in insertion order,
 * so without this a sub-modifier can render far from its parent.
 *
 * Two sources, in priority order:
 *  1. `depth` stamped by the backend (`cascade_order_modifiers`) — the rows are
 *     already in cascade order, so they are taken as given. The server only
 *     stamps it when the store flag is on, so its presence IS the gate.
 *  2. Otherwise the tree is derived here from `parent_parser_order_modifier`,
 *     which has been on the wire since the feature landed. This keeps the web
 *     app correct against an API that has not shipped the ordering yet.
 *
 * The list stays flat — callers keep iterating it exactly as before and only
 * add indentation. Nothing is dropped: rows whose parent is missing from the
 * list, or that sit in a cycle, come back at depth 0 rather than disappearing.
 *
 * Mirrors `order/sub_modifier_serialization.py` on the backend, the business
 * web app's copy of this file, and `ModifierCascade.kt` in the business app;
 * all four must agree.
 *
 * Note the distinction from `Modifier.parentModifierId`: that is the submit-side
 * key (a modifier OPTION id, sent to the backend when placing an order). This
 * reads `parent_parser_order_modifier`, the display-side key on a saved order
 * (the parent modifier ROW id, sent back by the backend).
 */

/** Matches the backend's MAX_SUB_MODIFIER_DEPTH (sub_modifier_constants.py). */
const MAX_DEPTH = 5;

export interface CascadedModifier {
  depth: number;
  [key: string]: any;
}

export function cascadeModifiers(mods: any[] | null | undefined): CascadedModifier[] {
  const rows = (mods || []).filter(m => m && typeof m === 'object');
  if (!rows.length) {
    return [];
  }

  // Server already cascaded these — trust its ordering and depths.
  if (rows.some(r => r.depth !== undefined && r.depth !== null)) {
    return rows.map(r => (r.depth === undefined || r.depth === null ? withDepth(r, 0) : r));
  }

  const present = new Set<number>(
    rows.map(r => r.id).filter(id => id !== undefined && id !== null),
  );

  const children = new Map<number, any[]>();
  const roots: any[] = [];
  for (const row of rows) {
    const parentId = row.parent_parser_order_modifier;
    // A parent outside this list is no parent we can draw under — is_extra rows
    // are filtered out of some payloads.
    if (parentId !== undefined && parentId !== null && present.has(parentId) && parentId !== row.id) {
      const bucket = children.get(parentId);
      bucket ? bucket.push(row) : children.set(parentId, [row]);
    } else {
      roots.push(row);
    }
  }
  if (!children.size) {
    return rows.map(r => withDepth(r, 0));
  }

  const ordered: CascadedModifier[] = [];
  const emitted = new Set<number>();
  // Iterative walk: the catalog caps nesting but the data is not trusted to
  // respect that cap, and a render must never blow the stack.
  for (const root of roots) {
    const stack: Array<[any, number]> = [[root, 0]];
    while (stack.length) {
      const [row, depth] = stack.pop() as [any, number];
      const id = row.id;
      // Guards a malformed parent chain: a row already drawn must not recurse.
      if (id !== undefined && id !== null) {
        if (emitted.has(id)) {
          continue;
        }
        emitted.add(id);
      }
      ordered.push(withDepth(row, depth));
      if (id === undefined || id === null) {
        continue;
      }
      // Past the cap the indent stops growing but the walk continues:
      // abandoning the descent would strand a legacy over-deep row at depth 0
      // at the end of the list, detached from its parent.
      const childDepth = Math.min(depth + 1, MAX_DEPTH);
      const kids = children.get(id);
      if (kids) {
        // Reversed so the pop order matches the caller's sibling order.
        for (let i = kids.length - 1; i >= 0; i--) {
          stack.push([kids[i], childDepth]);
        }
      }
    }
  }

  // Only reachable via a cycle among non-root rows — still has to render.
  if (ordered.length < rows.length) {
    for (const row of rows) {
      const id = row.id;
      if (id === undefined || id === null || !emitted.has(id)) {
        ordered.push(withDepth(row, 0));
      }
    }
  }
  return ordered;
}

/**
 * Stamps depth without mutating the caller's object — several screens rebuild
 * these rows into `Modifier` instances and re-render from the same array.
 */
function withDepth(row: any, depth: number): CascadedModifier {
  if (row.depth === depth) {
    return row;
  }
  const copy = Object.create(Object.getPrototypeOf(row));
  Object.assign(copy, row, { depth });
  return copy;
}
